import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Icon } from '../Brand'
import { AssistantMessage, renderText } from '../shared/AssistantMessage'
import { postChat } from '../../lib/api'
import { getThread } from '../../lib/sessionStore'
import { agentMeta } from './agentMeta'

const SECURITY_BLOCK = 'Request rejected by security policy.'

// Pull an { error } body out of a thrown request error whose message is the raw
// (possibly JSON) response text — so a security block still reads cleanly.
function errorFromThrow(e) {
  const msg = String(e?.message || '')
  try {
    const parsed = JSON.parse(msg)
    if (parsed && parsed.error) return parsed.error
  } catch { /* not JSON */ }
  return null
}

// One agent's threaded conversation. Mounted with key={agentName} so switching
// agents remounts and re-reads that agent's own thread — history stays isolated.
export default function AgentThread({ agent, user = null, session, onPersist = () => {} }) {
  const meta = agentMeta(agent.name)
  const thread = getThread(session, agent.name)
  const [messages, setMessages] = useState(thread.messages)
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const scrollRef = useRef(null)

  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [messages, busy])

  function commit(next) {
    thread.messages = next
    setMessages(next)
    onPersist()
  }

  async function send() {
    const prompt = input.trim()
    if (!prompt || busy) return
    setInput('')
    const withUser = [...messages, { role: 'user', text: prompt }]
    commit(withUser)
    setBusy(true)
    try {
      let data
      try {
        data = await postChat({
          prompt,
          agent: agent.name,
          sessionId: thread.sessionId,
          userId: user?.user_id,
        })
      } catch (e) {
        const err = errorFromThrow(e)
        if (err) data = { error: err }
        else throw e
      }
      if (data.error) {
        const text = data.error === SECURITY_BLOCK
          ? SECURITY_BLOCK
          : (typeof data.error === 'string' ? data.error : 'The request could not be completed.')
        commit([...withUser, { role: 'system', text }])
      } else if (!data.message && (data.artifacts || []).length === 0) {
        commit([...withUser, { role: 'system', text: 'The agent returned an empty response.' }])
      } else {
        commit([...withUser, {
          role: 'assistant',
          text: data.message || '',
          artifacts: data.artifacts || [],
          sources: data.sources || [],
          routedTo: Array.isArray(data.routed_to) ? data.routed_to : [],
          verdict: data.verdict || null,
        }])
      }
    } catch {
      commit([...withUser, {
        role: 'system',
        text: 'Could not reach the agent. Is the backend running on :8080?',
      }])
    } finally {
      setBusy(false)
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  const empty = messages.length === 0

  return (
    <div className="chat ws-thread">
      <div className="chat-head">
        <div className="eyebrow">{meta.label} agent</div>
        <h1>{meta.label}</h1>
        <p className="sub">Ask about {meta.hint}.</p>
      </div>

      <div className="chat-scroll" ref={scrollRef}>
        {empty && (
          <div className="chat-empty">
            <div className="cs-ic"><Icon name={meta.icon} /></div>
            <p>Ask the {meta.label} agent about {meta.hint}.</p>
          </div>
        )}

        <AnimatePresence initial={false}>
          {messages.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              {msg.role === 'user' && (
                <div className="chat-msg user">
                  <div className="chat-bubble user">{renderText(msg.text)}</div>
                </div>
              )}
              {msg.role === 'assistant' && <AssistantMessage msg={msg} />}
              {msg.role === 'system' && (
                <div className="chat-notice"><Icon name="info" />{msg.text}</div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        {busy && (
          <div className="chat-msg assistant">
            <div className="chat-avatar assistant"><Icon name="auto_awesome" /></div>
            <div className="chat-bubble">
              <span className="chat-typing"><i /><i /><i /></span>
            </div>
          </div>
        )}
      </div>

      <div className="chat-composer">
        <textarea
          className="chat-input"
          rows={1}
          value={input}
          placeholder={`Message the ${meta.label} agent…`}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={busy}
        />
        <button className="chat-send" onClick={send} disabled={busy || !input.trim()}>
          <Icon name="arrow_upward" />
        </button>
      </div>
    </div>
  )
}
